import useAuth from "./hooks/useAuth";
import { useEffect, useState, useCallback, useRef } from "react";
import Select from 'react-select';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const IntonationSnapshotView = ({ language, date, checkuser }) => {
    const { auth, setAuth } = useAuth();
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(date);
    const [playingAudio, setPlayingAudio] = useState(null);
    const [currentPlayingType, setCurrentPlayingType] = useState(null);
    const [loadingAudio, setLoadingAudio] = useState(null);
    const [currentPlayingResult, setCurrentPlayingResult] = useState(null);
    const [expandedResult, setExpandedResult] = useState(null);
    const [showPitch, setShowPitch] = useState(true); // true = Pitch, false = Intensity

    useEffect(() => {
        if (date && date !== 'All records' && date !== 'Select date') {
            console.log('Fetching intonation results for:', { language, date, checkuser });
            fetchIntonationResults();
        }
    }, [language, date, checkuser]);

    const fetchIntonationResults = async () => {
        setLoading(true);
        console.log('Starting to fetch intonation results...');
        
        try {
            const token = localStorage.getItem("token");
            let query = `${process.env.REACT_APP_USER_API_URL}/analysis/${language}/intonation_results`;
            
            const params = new URLSearchParams();
            params.append('date', date);
            if (checkuser) params.append('checkuser', checkuser);
            
            query += `?${params.toString()}`;

            console.log('Fetching from URL:', query);

            const response = await fetch(query, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                }
            });

            console.log('Response status:', response.status);

            if (response.ok) {
                const json = await response.json();
                console.log('Fetched results:', json);
                setResults(json);
            } else if (response.status === 401) {
                console.log('Unauthorized, removing token');
                localStorage.removeItem("token");
                setAuth({});
            } else {
                console.log('Response not OK:', response.status);
            }
        } catch (error) {
            console.error("Error fetching intonation results:", error);
        } finally {
            setLoading(false);
            console.log('Finished loading');
        }
    };

    const playAudio = async (audioPath, result, audioType) => {
        // Stop currently playing audio
        if (playingAudio) {
            playingAudio.pause();
            setPlayingAudio(null);
            setCurrentPlayingType(null);
            setCurrentPlayingResult(null);
        }

        const loadingKey = `${result.user_file}_${audioType}`;
        setLoadingAudio(loadingKey);

        try {
            const audioUrl = `${process.env.REACT_APP_USER_API_URL}/${audioPath}`;
            console.log('Playing audio:', audioUrl);
            
            const audio = new Audio(audioUrl);
            
            // Store the current result for segment tracking
            setCurrentPlayingResult(result);
            setCurrentPlayingType(audioType);

            let startTime = 0;
            let endTime = null;

            if (audioType === 'narrator') {
                startTime = parseFloat(result.start_time) || 0;
                endTime = parseFloat(result.end_time) || null;
                
                console.log(`Narrator segment: ${startTime}s to ${endTime}s`);
            }

            audio.addEventListener('loadeddata', () => {
                console.log('Audio loaded, duration:', audio.duration);
                setLoadingAudio(null);
                
                if (audioType === 'narrator' && startTime > 0) {
                    audio.currentTime = startTime;
                    console.log(`Set start time to: ${startTime}s`);
                }
                
                audio.play().then(() => {
                    setPlayingAudio(audio);
                    console.log('Audio started playing');
                }).catch(playError => {
                    console.error('Error playing audio:', playError);
                    setLoadingAudio(null);
                });
            });

            // Handle segment ending for narrator
            if (audioType === 'narrator' && endTime && endTime > startTime) {
                audio.addEventListener('timeupdate', () => {
                    if (audio.currentTime >= endTime) {
                        console.log(`Segment ended at: ${audio.currentTime}s`);
                        audio.pause();
                        setPlayingAudio(null);
                        setCurrentPlayingType(null);
                        setCurrentPlayingResult(null);
                    }
                });
            }

            audio.addEventListener('error', (e) => {
                console.error('Error loading audio:', audioPath, e);
                setLoadingAudio(null);
            });

            audio.addEventListener('ended', () => {
                console.log('Audio ended naturally');
                setPlayingAudio(null);
                setCurrentPlayingType(null);
                setCurrentPlayingResult(null);
            });

        } catch (error) {
            console.error('Error setting up audio:', error);
            setLoadingAudio(null);
            alert('Unable to play audio file. The file might not be accessible.');
        }
    };

    const stopAudio = () => {
        if (playingAudio) {
            console.log('Stopping audio');
            playingAudio.pause();
            playingAudio.currentTime = 0;
            setPlayingAudio(null);
            setCurrentPlayingType(null);
            setCurrentPlayingResult(null);
        }
        setLoadingAudio(null);
    };

    const handlePlayButtonClick = (result, audioType) => {
        if (isAudioPlaying(result, audioType)) {
            stopAudio();
        } else {
            const audioPath = audioType === 'user' ? result.user_file : result.narrator_file;
            playAudio(audioPath, result, audioType);
        }
    };

    const toggleExpandedView = (result) => {
        if (expandedResult === result.user_file) {
            setExpandedResult(null);
        } else {
            setExpandedResult(result.user_file);
        }
    };

    const preparePitchData = (result) => {
        if (!result.f0_user_dtw || !result.f0_narr_dtw) {
            return { userData: [], narratorData: [] };
        }

        const userPitch = result.f0_user_dtw.map(val => 
            val === null || val === undefined || isNaN(val) ? 0 : Number(val)
        );
        const narratorPitch = result.f0_narr_dtw.map(val => 
            val === null || val === undefined || isNaN(val) ? 0 : Number(val)
        );
        
        // Normalize time axes for comparison
        const maxLength = Math.max(userPitch.length, narratorPitch.length);
        const userData = [];
        const narratorData = [];

        for (let i = 0; i < maxLength; i++) {
            const timePoint = (i / maxLength * (result.end_time - result.start_time) + result.start_time).toFixed(2);

            if (i < userPitch.length) {
                userData.push({
                    time: timePoint,
                    value: userPitch[i],
                    normalizedIndex: i
                });
            }

            if (i < narratorPitch.length) {
                narratorData.push({
                    time: timePoint,
                    value: narratorPitch[i],
                    normalizedIndex: i
                });
            }
        }

        return { userData, narratorData };
    };

    const prepareIntensityData = (result) => {
        if (!result.intensity_user_dtw || !result.intensity_narr_dtw) {
            return { userData: [], narratorData: [] };
        }

        const userIntensity = result.intensity_user_dtw.map(val => 
            val === null || val === undefined || isNaN(val) ? 0 : Number(val)
        );
        const narratorIntensity = result.intensity_narr_dtw.map(val => 
            val === null || val === undefined || isNaN(val) ? 0 : Number(val)
        );
        
        const maxLength = Math.max(userIntensity.length, narratorIntensity.length);
        const userData = [];
        const narratorData = [];

        for (let i = 0; i < maxLength; i++) {
            const timePoint = (i / maxLength * (result.end_time - result.start_time) + result.start_time).toFixed(2);

            if (i < userIntensity.length) {
                userData.push({
                    time: timePoint,
                    value: userIntensity[i],
                    normalizedIndex: i
                });
            }

            if (i < narratorIntensity.length) {
                narratorData.push({
                    time: timePoint,
                    value: narratorIntensity[i],
                    normalizedIndex: i
                });
            }
        }

        return { userData, narratorData };
    };

    const safeFormatter = (value, name) => {
        // Handle null, undefined, or non-numeric values
        if (value === null || value === undefined || typeof value !== 'number' || isNaN(value)) {
            return [value, name];
        }
        return [value.toFixed(2), name];
    };

    const getMetricColor = (metric, value) => {
        if (metric.includes('DTW')) {
            if (value < 1.0) return 'text-green-600 bg-green-50';
            if (value < 2.0) return 'text-yellow-600 bg-yellow-50';
            return 'text-red-600 bg-red-50';
        } else if (metric.includes('correlation')) {
            if (value > 0.7) return 'text-green-600 bg-green-50';
            if (value > 0.4) return 'text-yellow-600 bg-yellow-50';
            return 'text-red-600 bg-red-50';
        }
        return 'text-gray-600 bg-gray-50';
    };

    const formatMetricValue = (metric, value) => {
        if (typeof value !== 'number') return value;
        
        if (metric.includes('DTW') || metric.includes('slope')) {
            return value.toFixed(3);
        } else if (metric.includes('correlation')) {
            return value.toFixed(3);
        }
        return value.toFixed(2);
    };

    const isAudioLoading = (result, audioType) => {
        return loadingAudio === `${result.user_file}_${audioType}`;
    };

    const isAudioPlaying = (result, audioType) => {
        return playingAudio && currentPlayingType === audioType && currentPlayingResult === result;
    };

    const getCurrentProgress = () => {
        if (!playingAudio || !currentPlayingResult || currentPlayingType !== 'narrator') return 0;
        
        const startTime = parseFloat(currentPlayingResult.start_time) || 0;
        const endTime = parseFloat(currentPlayingResult.end_time) || playingAudio.duration;
        const currentTime = playingAudio.currentTime;
        
        if (endTime <= startTime) return 0;
        
        const progress = ((currentTime - startTime) / (endTime - startTime)) * 100;
        return Math.max(0, Math.min(100, progress));
    };

    // Debug: log when component renders
    console.log('Component rendering:', { loading, results: results?.results?.length });

    if (loading) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="text-center">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-600 mt-2">Loading intonation results...</p>
                </div>
            </div>
        );
    }

    if (!results) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">📊</div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Data Loaded</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                    Click the refresh button to load intonation results for {date}.
                </p>
                <button
                    onClick={fetchIntonationResults}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Load Data
                </button>
            </div>
        );
    }

    if (!results.results || results.results.length === 0) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-400 text-4xl mb-2">🎵</div>
                <h3 className="text-lg font-semibold text-gray-600 mb-2">No Intonation Results Found</h3>
                <p className="text-gray-500 max-w-md mx-auto">
                    No intonation comparison results available for {date}. 
                    {date === 'All records' ? ' Run the comparison analysis first.' : ' Try selecting a different date or run the comparison analysis.'}
                </p>
                <button
                    onClick={fetchIntonationResults}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Refresh Data
                </button>
            </div>
        );
    }

    return (
        <div className="w-full max-w-full space-y-6">
            {/* Header */}
            <div className="text-center bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-200">
                <h2 className="text-2xl sm:text-3xl font-bold text-indigo-700">
                    Intonation Snapshot - {date}
                </h2>
                <p className="text-gray-600 mt-2">
                    Detailed intonation comparison results with audio playback
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                    <div className="bg-white px-3 py-1 rounded-full border border-indigo-200 text-sm text-indigo-700">
                        Results: {results.results.length}
                    </div>
                    <div className="bg-white px-3 py-1 rounded-full border border-indigo-200 text-sm text-indigo-700">
                        Language: {language}
                    </div>
                    <div className="bg-white px-3 py-1 rounded-full border border-indigo-200 text-sm text-indigo-700">
                        User: {checkuser}
                    </div>
                    <button
                        onClick={() => setShowPitch(!showPitch)}
                        className="bg-white px-3 py-1 rounded-full border border-indigo-200 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors"
                    >
                        Show {showPitch ? 'Intensity' : 'Pitch'}
                    </button>
                    <button
                        onClick={fetchIntonationResults}
                        className="bg-white px-3 py-1 rounded-full border border-indigo-200 text-sm text-indigo-700 hover:bg-indigo-50 transition-colors"
                    >
                        Refresh Data
                    </button>
                </div>
            </div>

            {/* Results Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {results.results.map((result, index) => {
                    const pitchData = preparePitchData(result);
                    const intensityData = prepareIntensityData(result);
                    const isExpanded = expandedResult === result.user_file;
                    const currentData = showPitch ? pitchData : intensityData;
                    const dataType = showPitch ? 'Pitch' : 'Intensity';
                    const userColor = "#3b82f6"; // Blue for user
                    const narratorColor = "#10b981"; // Green for narrator

                    return (
                        <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                            {/* File Header */}
                            <div className="mb-4">
                                <div className="flex justify-between items-start">
                                    <h3 className="font-semibold text-gray-800 text-sm mb-2 flex-1">
                                        {result.user_file?.split('/').pop() || 'Unknown file'}
                                    </h3>
                                    <button
                                        onClick={() => toggleExpandedView(result)}
                                        className="ml-2 px-2 py-1 bg-gray-100 hover:bg-gray-200 rounded text-xs text-gray-600 transition-colors"
                                    >
                                        {isExpanded ? '▲' : '▼'}
                                    </button>
                                </div>
                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                    <span>Start: {result.start_time}s</span>
                                    <span>End: {result.end_time}s</span>
                                    <span>Duration: {(result.end_time - result.start_time).toFixed(1)}s</span>
                                </div>
                            </div>

                            {/* Audio Controls */}
                            <div className="flex gap-2 mb-4">
                                {/* User Audio Button */}
                                <button
                                    onClick={() => handlePlayButtonClick(result, 'user')}
                                    disabled={isAudioLoading(result, 'user')}
                                    className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 ${
                                        isAudioPlaying(result, 'user') 
                                            ? 'bg-red-600 text-white hover:bg-red-700' 
                                            : isAudioLoading(result, 'user')
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-blue-600 text-white hover:bg-blue-700'
                                    }`}
                                >
                                    {isAudioLoading(result, 'user') ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Loading...
                                        </>
                                    ) : isAudioPlaying(result, 'user') ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Stop User
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            </svg>
                                            Play User
                                        </>
                                    )}
                                </button>

                                {/* Narrator Audio Button */}
                                <button
                                    onClick={() => handlePlayButtonClick(result, 'narrator')}
                                    disabled={isAudioLoading(result, 'narrator')}
                                    className={`flex-1 px-3 py-2 rounded-lg transition-colors text-sm flex items-center justify-center gap-2 ${
                                        isAudioPlaying(result, 'narrator') 
                                            ? 'bg-red-600 text-white hover:bg-red-700' 
                                            : isAudioLoading(result, 'narrator')
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    {isAudioLoading(result, 'narrator') ? (
                                        <>
                                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                            Loading...
                                        </>
                                    ) : isAudioPlaying(result, 'narrator') ? (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Stop Narrator
                                        </>
                                    ) : (
                                        <>
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                            </svg>
                                            Play Narrator
                                        </>
                                    )}
                                </button>
                            </div>

                            {/* Segment Info for Narrator */}
                            {currentPlayingType === 'narrator' && isAudioPlaying(result, 'narrator') && (
                                <div className="mb-3 p-2 bg-blue-50 rounded border border-blue-200 text-xs text-blue-700 text-center">
                                    <div>Playing segment: {result.start_time}s - {result.end_time}s</div>
                                    <div className="w-full bg-blue-200 rounded-full h-2 mt-1">
                                        <div 
                                            className="bg-blue-600 h-2 rounded-full transition-all duration-100"
                                            style={{ width: `${getCurrentProgress()}%` }}
                                        ></div>
                                    </div>
                                    <div className="text-xs text-blue-600 mt-1">
                                        Progress: {getCurrentProgress().toFixed(0)}%
                                    </div>
                                </div>
                            )}

                            {/* Expanded View with Separate Plots */}
                            {isExpanded && currentData.userData.length > 0 && (
                                <div className="mb-4 space-y-4">
                                    <h5 className="text-sm font-medium text-gray-700 mb-2">{dataType} Analysis</h5>
                                    
                                    {/* User Plot */}
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 mb-2">User {dataType}</h6>
                                        <div className="h-40">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={currentData.userData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                    <XAxis 
                                                        dataKey="time" 
                                                        tick={{ fontSize: 10 }}
                                                        label={{ value: 'Time (s)', position: 'insideBottom', offset: -5, fontSize: 10 }}
                                                    />
                                                    <YAxis 
                                                        tick={{ fontSize: 10 }}
                                                        label={{ value: dataType, angle: -90, position: 'insideLeft', fontSize: 10 }}
                                                    />
                                                    <Tooltip 
                                                        formatter={(value) => [value?.toFixed(2), dataType]}
                                                        labelFormatter={(label) => `Time: ${label}s`}
                                                    />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="value"
                                                        stroke={userColor}
                                                        strokeWidth={2}
                                                        dot={false}
                                                        name={`User ${dataType}`}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Narrator Plot */}
                                    <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                                        <h6 className="text-xs font-medium text-gray-600 mb-2">Narrator {dataType}</h6>
                                        <div className="h-40">
                                            <ResponsiveContainer width="100%" height="100%">
                                                <LineChart data={currentData.narratorData}>
                                                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                                    <XAxis 
                                                        dataKey="time" 
                                                        tick={{ fontSize: 10 }}
                                                        label={{ value: 'Time (s)', position: 'insideBottom', offset: -5, fontSize: 10 }}
                                                    />
                                                    <YAxis 
                                                        tick={{ fontSize: 10 }}
                                                        label={{ value: dataType, angle: -90, position: 'insideLeft', fontSize: 10 }}
                                                    />
                                                    <Tooltip 
                                                        formatter={(value) => [value?.toFixed(2), dataType]}
                                                        labelFormatter={(label) => `Time: ${label}s`}
                                                    />
                                                    <Line 
                                                        type="monotone" 
                                                        dataKey="value"
                                                        stroke={narratorColor}
                                                        strokeWidth={2}
                                                        dot={false}
                                                        name={`Narrator ${dataType}`}
                                                    />
                                                </LineChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>
                            )}
                            
                            {/* Show message if no data available */}
                            {isExpanded && currentData.userData.length === 0 && (
                                <div className="mb-4 p-3 bg-yellow-50 rounded border border-yellow-200 text-xs text-yellow-700 text-center">
                                    No {dataType.toLowerCase()} data available for this recording
                                </div>
                            )}

                            {/* Comparison Metrics */}
                            <div className="space-y-3">
                                <h4 className="font-medium text-gray-700 text-sm border-b pb-1">Comparison Metrics</h4>
                                {result.comparison_metrics && Object.entries(result.comparison_metrics).map(([metric, value]) => (
                                    <div key={metric} className="flex justify-between items-center">
                                        <span className="text-xs text-gray-600 capitalize">
                                            {metric.replace(/_/g, ' ')}:
                                        </span>
                                        <span className={`text-xs px-2 py-1 rounded ${getMetricColor(metric, value)}`}>
                                            {formatMetricValue(metric, value)}
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {/* Book Info */}
                            {result.author && (
                                <div className="mt-4 pt-4 border-t border-gray-100">
                                    <div className="text-xs text-gray-500 space-y-1">
                                        <div className="flex justify-between">
                                            <span>Author:</span>
                                            <span className="text-gray-700">{result.author}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Title:</span>
                                            <span className="text-gray-700">{result.title}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span>Type:</span>
                                            <span className="text-gray-700">{result.type}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Summary Stats */}
            {results.summary && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Daily Summary</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-2xl font-bold text-blue-700">{results.summary.total_comparisons}</div>
                            <div className="text-sm text-blue-600">Total Comparisons</div>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-2xl font-bold text-green-700">
                                {results.summary.average_correlation?.toFixed(3) || 'N/A'}
                            </div>
                            <div className="text-sm text-green-600">Avg Correlation</div>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-200">
                            <div className="text-2xl font-bold text-purple-700">
                                {results.summary.average_dtw?.toFixed(3) || 'N/A'}
                            </div>
                            <div className="text-sm text-purple-600">Avg DTW Distance</div>
                        </div>
                        <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-200">
                            <div className="text-2xl font-bold text-orange-700">
                                {results.summary.success_rate?.toFixed(1) || '0'}%
                            </div>
                            <div className="text-sm text-orange-600">Success Rate</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IntonationSnapshotView;